import { useContext, type ReactNode } from "react"
import { Link, useNavigate } from "react-router-dom"
import { track } from "@/lib/analytics"
import { requireLogin } from "@/lib/authGate"
import { AuthContext } from "@/providers/AuthProvider"
import { isDarkTheme, toggleTheme, useIsDark } from "../answer/hooks"
import { useBeam } from "../beam"
import type { ThreadContext } from "../readerContext"
import { Beam, type BeamState } from "./Beam"
import { ContextMenu } from "./ContextMenu"
import { usePopover } from "./usePopover"
import "./bar.css"
import "./ds.css"

interface NiraBarProps {
  /** Opens the history drawer; the button is left out when absent. */
  onMenu?: () => void
  menuOpen?: boolean
  menuControls?: string
  /** What the page is about, after the wordmark: the question or the matter. */
  crumb?: string
  /** Where the page sits, after the wordmark: each step but the last is a link back. Overrides `crumb`. */
  crumbs?: Crumb[]
  /** The Matter chip, after the trail; it keeps its width when the trail shortens. */
  matter?: ReactNode
  /** Page actions on the right, before the theme switch and the avatar. */
  actions?: ReactNode
  /** The beam beside the wordmark mirrors what Nira is doing on the page; the beam store by default. */
  beam?: BeamState
  /** The context chip: the State and side the answers are for. */
  context?: { value: ThreadContext; onChange: (ctx: ThreadContext) => void; note?: string; onRerun?: () => void; rerunBusy?: boolean }
  homeTo?: string
  /** Primary navigation (Workspaces, and Sign in for a guest); for pages that stand on their own. A guest is sent through sign-in first. */
  nav?: { signedIn: boolean }
}

export interface Crumb { label: string; to?: string }

const MenuGlyph = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <path d="M3 6h14M3 10h14M3 14h14" />
  </svg>
)

/** The 56px top bar of every Nira page: menu, lockup, crumb, actions, theme, avatar. */
export function NiraBar({ onMenu, menuOpen, menuControls, crumb, crumbs, actions, matter, beam, context, homeTo = "/ask", nav }: NiraBarProps) {
  const live = useBeam()
  const state = beam ?? live.state
  const trail: Crumb[] = crumbs ?? (crumb ? [{ label: crumb }] : [])
  // On a phone the trail folds to one link: back to the step above this page.
  const up = [...trail].reverse().find((c) => c.to)
  return (
    <header className="nr nr-bar">
      <div className="nr-bar-l">
        {onMenu ? (
          <button
            type="button"
            className="nr-bar-ib"
            aria-label="Menu and history"
            aria-expanded={menuOpen}
            aria-controls={menuControls}
            onClick={onMenu}
          >
            {MenuGlyph}
          </button>
        ) : null}
        {up?.to ? (
          <Link className="nr-bar-back" to={up.to} aria-label={`Back to ${up.label}`} title={`Back to ${up.label}`}>
            <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12.5 4.5 7 10l5.5 5.5" /></svg>
            <span className="nr-bar-back-w">Back</span>
            <span className="nr-bar-back-l">{up.label}</span>
          </Link>
        ) : null}
        <Link className="nr-wordmark" to={homeTo} aria-label="Nira home" title="Home">
          <Beam state={state} label={null} lean={live.lean} />
          <span className="nr-wordmark-text" aria-hidden="true">nira</span>
        </Link>
        {trail.length ? (
          <nav className="nr-bar-trail" aria-label="Where you are">
            {trail.map((c, i) => (
              <span className="nr-bar-step" key={`${i}:${c.label}`}>
                <span className="nr-bar-sep" aria-hidden="true">/</span>
                {c.to && i < trail.length - 1 ? (
                  <Link className="nr-bar-crumb link" to={c.to} title={c.label}>{c.label}</Link>
                ) : (
                  <span className="nr-bar-crumb" title={c.label} aria-current={i === trail.length - 1 ? "page" : undefined}>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        ) : null}
        {matter ? <div className="nr-bar-matter">{matter}</div> : null}
        {nav ? (
          <nav className="nr-bar-nav" aria-label="Primary">
            {nav.signedIn ? (
              <Link className="nr-bar-navlink" to="/workspaces" onClick={() => track("workspaces_opened", { from: "top_bar" })}>Workspaces</Link>
            ) : (
              <button type="button" className="nr-bar-navlink" onClick={() => requireLogin("login_required", "/workspaces")}>Workspaces</button>
            )}
          </nav>
        ) : null}
      </div>
      <div className="nr-bar-r">
        {context ? <div className="nr-bar-state"><ContextMenu ctx={context.value} onChange={context.onChange} note={context.note} onRerun={context.onRerun} rerunBusy={context.rerunBusy} /></div> : null}
        {actions ? <div className="nr-bar-acts">{actions}</div> : null}
        {actions ? <MoreMenu>{actions}</MoreMenu> : null}
        {nav && !nav.signedIn ? (
          <button type="button" className="nr-btn nr-bar-signin" data-variant="quiet" onClick={() => requireLogin("login_required")}>Sign in</button>
        ) : null}
        <AvatarMenu />
      </div>
    </header>
  )
}

/** On a phone the page's actions (copy, print, share) fold into one ⋯ menu (handoff 3 §11). */
function MoreMenu({ children }: { children: ReactNode }) {
  const { open, setOpen, button, panel } = usePopover()
  return (
    <div className="nr-ctx nr-bar-more">
      <button ref={button} type="button" className="nr-bar-ib" aria-label="Copy, print and share" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <svg viewBox="0 0 20 20" width="18" height="18" fill="currentColor" aria-hidden="true"><circle cx="4.5" cy="10" r="1.6" /><circle cx="10" cy="10" r="1.6" /><circle cx="15.5" cy="10" r="1.6" /></svg>
      </button>
      {open ? (
        <div ref={panel} className="nr nr-menu nr-menu-end nr-more-menu" role="menu" aria-label="Copy, print and share" onClick={() => setOpen(false)}>
          {children}
        </div>
      ) : null}
    </div>
  )
}

/** The avatar and its menu: Profile and Subscription, the theme, and signing out. */
export function AvatarMenu() {
  const auth = useContext(AuthContext)
  const user = auth?.session?.user
  const name = user?.displayName || user?.email || ""
  const dark = useIsDark()
  const navigate = useNavigate()
  const { open, setOpen, button, panel } = usePopover()
  const go = (to: string, event: "profile_opened" | "subscription_opened" | "workspaces_opened") => {
    track(event, { from: "avatar_menu" })
    setOpen(false)
    navigate(to)
  }
  return (
    <div className="nr-ctx">
      <button
        ref={button}
        type="button"
        className="nr-bar-avatar"
        aria-label={name ? `Account: ${name}` : "Settings"}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {name ? name.trim().charAt(0).toUpperCase() : "·"}
      </button>
      {open ? (
        <div ref={panel} className="nr nr-menu nr-menu-end" role="menu" aria-label="Account">
          {user?.email ? <p className="nr-menu-who">{user.email}</p> : null}
          {user?.email ? (
            <>
              <button type="button" role="menuitem" className="nr-menu-item" onClick={() => go("/workspaces", "workspaces_opened")}>
                Workspaces
              </button>
              <button type="button" role="menuitem" className="nr-menu-item" onClick={() => go("/account", "profile_opened")}>
                Profile
              </button>
              <button type="button" role="menuitem" className="nr-menu-item" onClick={() => go("/account/subscription", "subscription_opened")}>
                Subscription
              </button>
            </>
          ) : null}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={dark}
            className="nr-menu-item"
            onClick={() => toggleTheme()}
          >
            <span>Dark theme</span>
            <span className="nr-switch" data-on={isDarkTheme() ? "true" : "false"} aria-hidden="true" />
          </button>
          {auth?.logout && user?.email && !user.email.endsWith(".local") ? (
            <button type="button" role="menuitem" className="nr-menu-item" onClick={() => void auth.logout()}>
              Sign out
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
